import { Component, OnInit, ChangeDetectorRef } from "@angular/core";
import { ClubService } from "../providers/club.service";
import { ProfileService } from "../providers/profile.service";
import { ApiService } from "../providers/api.service";
import { BvDialogService } from "../design-system";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { AlertDialogComponent } from "../shared/alert-dialog/alert-dialog.component";
import { ConfirmDialogComponent } from "../shared/confirm-dialog/confirm-dialog.component";
import { LoadingOverlayComponent } from "../shared/loading-overlay/loading-overlay.component";
import { BvDialogTitleComponent, BvDialogBodyComponent, BvDialogFooterComponent } from "../design-system/primitives/dialog.component";

import { FormsModule } from "@angular/forms";
import { BvFormFieldComponent } from "../design-system/primitives/form-field.component";
import { BvInputDirective } from "../design-system/primitives/input.directive";
import { BvButtonComponent } from "../design-system/primitives/button.component";
import { BvIconComponent } from "../design-system/primitives/icon.component";
import { FeatureLockComponent } from "../shared/feature-lock/feature-lock.component";
import { FileInputComponent } from "../shared/file-input/file-input.component";
import { RouterLink } from "@angular/router";
import { CloseDialogButtonComponent } from "../shared/close-dialog-button/close-dialog-button.component";
import { ClubLocationComponent } from "../club-location/club-location.component";
import { BvBadgeComponent } from "../design-system/primitives/badge.component";
import { BvRadioGroupComponent, BvRadioComponent } from "../design-system/primitives/radio.component";
import { NgTemplateOutlet } from "@angular/common";
import { ClubJoinLinkComponent } from "./club-join-link/club-join-link.component";

@Component({
    selector: "app-club",
    templateUrl: "./club.component.html",
    styleUrls: ["./club.component.scss"],
    imports: [LoadingOverlayComponent, BvDialogTitleComponent, BvDialogBodyComponent, FormsModule, BvFormFieldComponent, BvInputDirective, BvButtonComponent, BvIconComponent, FeatureLockComponent, FileInputComponent, RouterLink, BvDialogFooterComponent, CloseDialogButtonComponent, BvBadgeComponent, BvRadioGroupComponent, BvRadioComponent, NgTemplateOutlet, ClubJoinLinkComponent, TranslateModule]
})
export class ClubComponent implements OnInit {
  clubName!: string;
  clubPass!: string;
  clubLogoURL!: string | null;
  backURL!: string | null;
  clubLogoFile!: File | null;
  backFile!: File | null;
  uploading: boolean = false;
  // "approval" (default): join requests need Owner/Staff approval.
  // "open": anyone with the club link joins instantly.
  joinPolicy: "approval" | "open" = "approval";
  constructor(
    public clubServ: ClubService,
    public dialog: BvDialogService,
    private translate: TranslateService,
    public profServ: ProfileService,
    private api: ApiService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.clubName = this.clubServ.latestClub.clubName;
    this.clubLogoURL = this.clubServ.latestClub.logoURL;
    this.backURL = this.clubServ.latestClub.backgroundURL;
    this.joinPolicy =
      this.clubServ.latestClub.joinPolicy === "open" ? "open" : "approval";
    this.profServ.disableDeleteClub();
  }
  // Locations are managed from this dialog: the editor stacks on top and
  // closes only itself, so the list below (clubServ.locations signal)
  // updates live while settings stay open. Home is set in the editor.
  addLocation() {
    this.dialog.open(ClubLocationComponent, { width: "520px" });
  }
  editLocation(locationID: string) {
    this.dialog.open(ClubLocationComponent, {
      width: "520px",
      data: { locationID },
    });
  }
  isHomeLocation(loc: any): boolean {
    return !!loc?.locationID && loc.locationID === this.clubServ.latestClub?.homeLocationID;
  }
  changeClub() {
    this.uploading = true;
    let promLogoUrl = null;
    let promBackgroundUrl = null;
    if (this.clubLogoFile || this.backFile) {
      if (this.clubLogoFile) {
        promLogoUrl = this.clubServ.uploadClubLogo(this.clubLogoFile);
        this.api.sendEvent(
          "EVT_CLUB_LOGO_UPDATED",
          this.profServ.latestProfile
        );
      }
      if (this.backFile) {
        promBackgroundUrl = this.clubServ.uploadBackImg(this.backFile);
        this.api.sendEvent(
          "EVT_CLOCK_BACKGROUND_UPDATED",
          this.profServ.latestProfile
        );
      }
      Promise.all([promLogoUrl, promBackgroundUrl])
        .then((res) => {
          this.uploading = false;
          this.cdr.markForCheck();
          this.clubServ.updateClub(this.clubName, res[0], res[1], this.joinPolicy);
          this.dialog.closeAll();
        })
        .catch((err) => {
          this.translate
            .get(["FILE_UPLOAD_FAILED", "CLOSE"])
            .subscribe((res: any) => {
              this.dialog.open(AlertDialogComponent, {
                data: {
                  message: res["FILE_UPLOAD_FAILED"],
                  closeButton: res["CLOSE"],
                },
                panelClass: "alert-dialog-container",
              });
            });
          this.uploading = false;
          this.cdr.markForCheck();
        });
    } else {
      this.clubServ.updateClub(this.clubName, undefined, undefined, this.joinPolicy);
      this.api.sendEvent(
        "EVT_CLUB_SETTINGS_UPDATED",
        this.profServ.latestProfile
      );
      this.dialog.closeAll();
    }
  }
  logoChange(file: any) {
    var reader = new FileReader();

    reader.onload = (e: any) => {
      this.clubLogoURL = e.target.result;
      this.cdr.markForCheck(); // FileReader onload — notify CD under zoneless
    };

    reader.readAsDataURL(file);
    this.clubLogoFile = file;
  }
  backChange(file: any) {
    var reader = new FileReader();

    reader.onload = (e: any) => {
      this.backURL = e.target.result;
      this.cdr.markForCheck(); // FileReader onload — notify CD under zoneless
    };

    reader.readAsDataURL(file);
    this.backFile = file;
  }
  deleteClub() {
    this.translate
      .get(["CONFIRM_DELETE", "CONFIRM", "CANCEL"])
      .subscribe((res: any) => {
        if (this.clubServ.latestClub) {
          this.dialog
            .open(ConfirmDialogComponent, {
              data: {
                message: this.clubServ.latestClub.clubName,
                title: res["CONFIRM_DELETE"],
                acceptButton: res["CONFIRM"],
                cancelButton: res["CANCEL"],
              },
              panelClass: "confirm-dialog-container",
            })
            .afterClosed()
            .subscribe((accept: boolean) => {
              if (accept) {
                this.clubServ.deleteClub();
                // No optimistic splice of latestClubs (it's now an immutable
                // signal value); the RTDB round-trip from deleteClub drives the
                // clubs signal, and loadFirstClub already skips deletedClub.
                this.profServ.loadFirstClub();
                this.dialog.closeAll();
              }
            });
        }
      });
  }
  removeClubImage(clubImgType: string) {
    this.translate
      .get(["CONFIRM_DELETE", "CONFIRM", "CANCEL", clubImgType])
      .subscribe((res: any) => {
        this.dialog
          .open(ConfirmDialogComponent, {
            data: {
              message: res[clubImgType],
              title: res["CONFIRM_DELETE"],
              acceptButton: res["CONFIRM"],
              cancelButton: res["CANCEL"],
            },
            panelClass: "confirm-dialog-container",
          })
          .afterClosed()
          .subscribe((accept: boolean) => {
            if (accept) {
              this.clubServ.removeClubImage(clubImgType);
              if (clubImgType == "CLOCK_BACKGROUND_IMAGE") {
                this.backURL = null;
                this.backFile = null;
              } else {
                this.clubLogoURL = null;
                this.clubLogoFile = null;
              }
              this.dialog.closeAll();
              this.cdr.markForCheck();
            }
          });
      });
  }
}
